<template>
<div class="chapter">
<div class="inner_page">
<h1 class="cn"><a href="#/SeducedByARogu_toc#chap-1"><em>Chapter 1</em></a></h1>
<p class="da"><em>Dunwythie Mains, Annandale, 8 March 1375</em></p>
<p class="cotx"><span class="dropcap">P</span>eering through new green foliage into a large field that the surrounding woodland sheltered from winds that could roar up
         the dale from Solway Firth, Will Jardine said, “What if Dunwythie catches us here?”
      </p>
<p>“He won’t,” twenty-five-year-old Robert Maxwell, Laird of Trailinghail, replied as they dismounted in the dense woods. “My
         lads saw his lordship ride north with ten men just after dawn.” Looping his reins over a handy branch, he added, “He will
         be gone till at least midday, Will. And we have every right to be here.”
      </p>
<p>The younger man’s eyebrows shot upward. “Have we?” he said dryly. “Most Annandale folk would dispute that, including me own
         da, were ye daft enough to put your brother’s impertinent demands to <em>him</em>.”
      </p>
<p>“Alex’s demands are hardly impertinent, since he is Sheriff of Dumfries.”</p>
<p>“Aye, but <em>only</em> o’ Dumfries,” Will retorted. “Nae one here in Annandale heeds or needs the man, least of all Lord Dunwythie.”
      </p>
<p>Unable to deny Annandale’s defiance or Dunwythie’s, Rob kept silent. He was watching where he put his feet as he and Will
         strode across the field toward a dozen or so men working on the far side. It would not do to give the recalcitrant Dunwythie
         more cause for complaint by trampling his tender young shoots.
      </p>
<p>“God bethankit for His gifts!” Will exclaimed. “What d’ye think can ha’ brought the two o’ <em>them</em> here?”
      </p>
<p>Rob looked up.</p>
<p>Emerging from woods north of them onto a narrow path down the center of the field were two riders. Although they were nearly
         a quarter mile away, their gowns, fur-lined cloaks, and fluttering white veils proclaimed them noblewomen. Their figures and
         their supple dexterity with their horses declared them youthful.
      </p>
<p>As they drew nearer, Rob saw that one was so fair that her hair looked white against her dark green cloak. The other was dark-haired,
         and both wore their hair in long plaits that bobbed enticingly on their breasts as they rode. They were watching the workers,
         and he was sure neither had yet realized that he and Will—in their leather breeks, jacks, and boots—were not simply two more
         of them.
      </p>
<p>A few puffy white clouds floated overhead but did little to block the sun. Its light glistened on the dewy green field and
         gilded the fair rider’s plaits.
      </p>
<p>“I’m glad I came with ye,” Will murmured with a wicked gleam in his eyes.</p>
<p>“They are noblewomen, you lecherous ruffian.”</p>
<p>“Hoots, what noblewomen would ride alone here as those two are doing?”</p>
<p>“Dunwythie’s daughters would do so on their father’s land, a mile from his castle, amidst his own loyal workmen,” Rob said.
         “Behave yourself now.”
      </p>
<p>“I’ve nae wish to frighten off such tasty morsels,” Will retorted, chuckling.</p>
<p>Rob grimaced, knowing his friend’s reputation with women. Glancing back at the two riders, he saw the fair one frown.</p>
<p>Clearly, she had realized they were intruders.</p>
<p>“We’ll go to meet them,” he told Will. “And you <em>will</em> behave.”
      </p>
<p>“Aye, sure. Wi’ such toothsome lassies, I’ll behave right charmingly.”</p>
<p>Rob sighed and altered his course to meet the two, hoping he could avoid trouble with Will. Old Jardine being the Maxwells’
         only ally in Annandale, Rob could not afford to anger the man’s best-favored and sole remaining son. But neither would he
         let Will make free and easy with Dunwythie’s daughters.
      </p>
<p class="img"><img alt="art" src="images/Art_orn.jpg"/></p>
<p class="spb">“Who are those two men?” the lady Fiona Dunwythie asked, pushing a dark curl away from one long-lashed blue eye to tuck it
         back under her veil.
      </p>
<p>“I don’t know,” nineteen-year-old Mairi Dunwythie replied. Wishing—not for the first time—that she knew more people in the
         area near her father’s largest estate, she added, “They stride toward us like men aware of their worth.”
      </p>
<p>“Then where are their horses?” Fiona demanded. “In my experience, men who know their worth rarely walk far.”</p>
<p>“Doubtless they left them in the woods behind them,” Mairi said.</p>
<p>“Then they’ll have come from the south,” Fiona said thoughtfully. “I wonder if they might be Jardines.”</p>
<p>That the two strangers might be members of that obstreperous family had already occurred to Mairi. However, although she had
         begun her life at Dunwythie Mains, she knew few of its neighbors by sight.
      </p>
<p>Three years after the lady Elspeth, her mother, had died at Mairi’s birth, Mairi’s father had married the lady Phaeline Douglas.
         Learning soon after their marriage that the Jardines to the south of them and the Johnstones to the north were engaged in
         longstanding, nearly continuous warfare, Phaeline had demanded that her husband remove his family to the house near Annan
         town that represented the primary part of her tocher, or dowry.
      </p>
<p>At the time, Phaeline had been pregnant with Fiona, so her lord had readily complied. Thus, Fiona was born at Annan House,
         near the mouth of the river, and Mairi had lived there from the age of four, with only occasional brief visits upriver to
         Dunwythie Mains.
      </p>
<p>Whether the two men striding to meet them were Jardines or not, Mairi knew her father would expect her to welcome them, albeit
         with no more than cool civility.
      </p>
<p>Discerning eagerness now in her sister’s posture, she said in her usual quiet way, “Prithee, dearling, do not be making much
         of these men. If they <em>are</em> Jardines, our lord father will not want us to encourage more such visits.”
      </p>
<p>Tossing her head, Fiona said, “Certes, Mairi, Father would not want us to be discourteous, either. And they are both gey handsome.”</p>
<p>Mairi had noted that fact as well. Both were large, dark-haired men with well-formed features. The one in the lead was narrow
         through hips and waist, had powerful looking thighs and shoulders, and stood inches taller than his companion.
      </p>
<p>He also looked five or six years older and displayed a demeanor that suggested he was accustomed to command, and to doing
         as he pleased. He had worn his leather breeks and boots often enough that they molded themselves snugly to his form. The shirt
         showing beneath his dark green jack was snowy white.
      </p>
<p>As they drew nearer, she saw that his boots were of expensive tanned leather, not rawhide. He also wore a fan brooch of three
         short reddish-brown feathers pinned with a small but brightly sparkling emerald in the soft folds of his hat. Sunbeams painted
         similar reddish highlights in his dark brown hair.
      </p>
<p>The younger man had black hair, a lankier body, and looked nearer her own age. He was eyeing Fiona in a way that made Mairi
         think of a hawk eyeing a tasty-looking rabbit.
      </p>
<p>Clearly oblivious to the predatory look, Fiona smiled flirtatiously enough to make her sister yearn to scold her. But Mairi
         held her tongue and shifted her gaze back to the two visitors.
      </p>
<p>“Well met, my ladies,” the younger one said as the women drew rein. “What are two such bonnie lasses doing, riding amongst
         these rough field men?”
      </p>
<p>Stiffening, Mairi put up a hand to silence Fiona just as the older man clamped a hand to the brash one’s shoulder. Her own
         gesture failed of its aim, for Fiona said pertly and with an arch look at the younger one, “But who are <em>you</em>, sir, to address us so rudely? And <em>what</em> are you doing in our barley field?”
      </p>
<p>“Pray, forgive us, my lady,” the larger man said, looking at Mairi with long-lashed eyes of such a clear ice-blue that she
         could almost see her reflection in them. His voice was deep and of a nature to send strange sensations through her, as if
         its gentle vibrations touched every nerve in her body.
      </p>
<p>Still looking right at her, he said, “I am Robert Maxwell of Trailinghail. This unmannerly chuff with me is William Jardine
         of Applegarth. I expect you must be Dunwythie’s daughters, are you not?”
      </p>
<p>Mairi nodded, touching Fiona’s arm as she did in a hope that the gesture would silence her, at least briefly. Then she said,
         “You must know that you are on my father’s land, sir. Have you reason to be?”
      </p>
<p>“Good reason, my lady,” he replied. “I am Sheriff Alexander Maxwell’s brother, here today as his sheriff-substitute.”</p>
<p>For a wonder, Fiona kept silent, perhaps as captivated by the man’s low, purring voice as Mairi was.</p>
<p>“But why do you come here?” Mairi asked, although she could guess. Her father had spoken often of the sheriff.</p>
<p>“Why, to determine the exact amount your lord father will owe the Crown in taxes this year,” he said. “Sithee, one determines
         the figure by counting everyone on the estate, measuring its size, and estimating its likely crop yield.”
      </p>
<p>Mairi knew that. Her father had recently been teaching both of his daughters about running his estates, as protection against
         the possibility that his lady wife might fail to give him a son to inherit them. Phaeline had been pregnant many times during
         their sixteen-year marriage, but so far, she had produced only Fiona.
      </p>
<p>Dunwythie had long agreed with his lady that, in due time, God would grant them a son. But, at last, urged by Phaeline’s elder
         brother, he had decided to teach his daughters what each would need to know if she should inherit his estates.
      </p>
<p>The estates’ crops being a primary source of his lordship’s wealth in a region where few men had any, he had brought Mairi
         and Fiona to Dunwythie Mains to observe the progress of the early plantings there.
      </p>
<p>Despite her recently acquired knowledge, Mairi was reluctant to cross words with the sheriff’s brother. Just meeting his gaze
         made her feel dangerously vulnerable, as if without effort he had melted her defenses and would as easily demolish any position
         she might take in trying to persuade him to leave.
      </p>
<p>As she sought tactful words to tell the two men they would have to wait and deal directly with her father, her outspoken sister
         said, “Surely, the two of you should not be prowling here for <em>any</em> reason without my lord father’s consent.”
      </p>
<p>“Did ye no hear him, lass?” William Jardine said, leering. “Rob acts for the sheriff. And the sheriff, as even such a bonnie
         lass must know, has vast powers.”
      </p>
<p>Tossing her head again, Fiona said, “Even so, William Jardine, that does not explain what right <em>you</em> have to trespass on our land.”
      </p>
<p>“I go where I please, lassie. And as I’m thinking I shall soon give your wee, winsome self good cause to ken me fine, ye should
         call me Will. Nae one calls me William except me da when he’s crabbit or cross.”
      </p>
<p>“Enough, Will,” Maxwell said as he met Mairi’s gaze with a rueful look in his distractingly clear eyes.</p>
<p>Despite her certainty that he would soon clash with her father, Mairi’s heart beat faster, radiating heat all the way to her
         cheeks.
      </p>
<p>Robert Maxwell smiled, revealing strong white teeth. His eyes twinkled, too, as if he sensed the inexplicable attraction she
         felt toward him.
      </p>
<p>Was he as arrogant and sure of himself, then, as his friend Will Jardine was?</p>
<p class="img"><img alt="art" src="images/Art_orn.jpg"/></p>
<p class="spb">Noting her reddening cheeks and the quizzical look in her gray eyes, Rob felt an immediate and unusually powerful reaction
         that he could not readily define.
      </p>
<p>She looked so small and fragile on her horse, and so extraordinarily fair that the light dusting of freckles across her nose
         and cheeks seemed out of place, as if she had been more often in the sun than usual. But as he returned her disturbingly steady
         gaze, he sensed serenity and an inner strength that warned him to tread lightly. It also made him glad that he had made the
         effort to silence Will.
      </p>
<p>She seemed oddly familiar, as though he knew how she would move and what she might say next, as if he had recognized the soft,
         throaty nature of her voice, even the confident way she held both reins in one smoothly gloved hand.
      </p>
<p>Despite his having met her just minutes before, the feeling was, he thought, the sort a man might have if she had occupied
         his thoughts before, and often. He realized he was smiling—as if he were delighted to be meeting her at last.
      </p>
<p>That notion being plainly daft, he tried to dismiss it. He saw then that her light blushes had deepened to a painful looking
         red spreading to the roots of her hair.
      </p>
<p>Hastily, and without looking at Will, Rob said, “I hope you can forgive the lad’s impudence, and mine own, my lady. Is there
         aught else you would ask of me?”
      </p>
<p>“You are kind to explain things,” she said. “But as we are only women”—Rob saw the younger lass cast an astonished look at
         her—“you would be wiser and would accomplish more, I am sure, by explaining yourself instead to our lord father.”
      </p>
<p>He would speak to Dunwythie later. Now, though, he smiled again, ignoring instinct that warned him he might be making a mistake
         to press her. “You can save us much time if you will just tell us how large your estates are,” he said. “Men talk often of
         the size and value of their holdings, do they not?”
      </p>
<p>The serene gray eyes flashed, but her voice remained calm.</p>
<p>“Not to their womenfolk, sir,” she said. “I doubt you would take my word for their size if I <em>could</em> tell you. My father will return this afternoon. You can talk with him then. Come, dearling,” she said to her sister. “We
         must go.”
      </p>
<p>With a nudge of heel and a twitch of reins, she turned her horse and rode back the way she had come.</p>
<p>Her sister followed, reluctantly and only after a last twinkling smile for Will.</p>
<p>Rob watched the two young women until they vanished into the woods.</p>
<p>“Sakes, Rob, ha’ ye lost your wits? Ye stared at that lass like a right dafty.”</p>
<p>“Unless you want me to teach you manners, Will Jardine, keep silent until you have something worth hearing to say to me.”</p>
<p>“Och, aye, I’m mute,” Will said, looking warily at Rob’s hands.</p>
<p>Realizing he had clenched both into fists, Rob drew a breath, let it out slowly, and relaxed them.</p>
<p>“Aye, that’s better,” Will said with relief. “What now?”</p>
<p>“We get our horses and view the other fields,” Rob said, fighting an urge to look again at the place where the women had gone
         into the woods.
      </p>
<p>What on earth was wrong with him, he wondered, that he could let one young female affect him so?</p>
<p class="img"><img alt="art" src="images/Art_orn.jpg"/></p>
<p class="spb">“I do think you might have been more helpful, Mairi. ‘<em>Only</em> women,’ indeed!”
      </p>
<p>Grateful that Fiona had at least waited until they were beyond earshot of their visitors before commenting, Mairi forced a
         strong image of the disquieting Robert Maxwell from her mind as she gravely eyed her sister.
      </p>
<p>When his image threatened to return, she said more forcefully than she had intended, “You flirted dreadfully with William
         Jardine, Fiona! You must not! You <em>know</em> Father wants us to keep clear of <em>all</em> Jardines.”
      </p>
<p>“Pish tush,” Fiona said without remorse. “I do not understand how anyone can imagine that such a handsome, charming gentleman
         as Will Jardine can be aught but a friend to us.”
      </p>
<p>“He may be handsome, but he was not charming,” Mairi said. “He was cheeky and rude. And he behaved as if he thought he had
         every right to treat you disrespectfully. You should never respond to such behavior as you did.”
      </p>
<p>“A fine one <em>you</em> are to say such a thing! You blushed at every word Robert Maxwell said to you.”
      </p>
<p>“I did not,” Mairi said, hoping she spoke the truth. Even now, his powerful image intruded.</p>
<p>Catching Fiona’s shrewd gaze on her, she added quickly, “If I did blush, I will not do so again. He wants to help the sheriff
         extend his authority into Annandale, so he is no friend to us. Neither the Maxwells nor the Jardines are our friends, Fiona.
         We must both remember that.”
      </p>
<p>“Aye, well, I think we should <em>make</em> them our friends,” Fiona said with a teasing look. “Surely, making friends is better than going on as enemies.”
      </p>
<p>“It is not as easy to do that as to suggest it,” Mairi said. “Recall that our father told us the troubles between the Jardines
         and the other Annandale noblemen began long ago, in the days of Annandale’s own Robert the Bruce. The Maxwells and Jardines
         sided with England, against the Bruce becoming King of Scots.”
      </p>
<p>“Pooh,” Fiona said. “That’s just history and too long ago to matter to anyone. This is <em>now</em>, Mairi, and Will Jardine is one of the handsomest men I’ve ever seen.”
      </p>
<p>“Fiona—”</p>
<p>“The truth is you’ve gone so long without an eligible suitor that you should welcome attentions from a man 
         as handsome as Robert Maxwell. To be sure, he is old… at least five-and-twenty… and not nearly as good-looking as Will Jardine.
         But you are only six years younger, Mairi! And Robert Maxwell <em>is</em> handsome. Moreover, you cannot deny that he intrigued you enough to make you blush.”
      </p>
<p>Mairi did not try to deny it. Instead, repressively, she said, “His brother is abusing the power of his office to extort money
         from the lairds of Annandale. We lie outside his jurisdiction, Fiona. And as your would-be friend William Jardine is clearly
         abetting the Maxwells, we have no more to discuss about <em>him</em>.”
      </p>
<p>Fiona gave her a speaking look but did not otherwise reply.</p>
<p>Sakes, Mairi thought as the image of Robert Maxwell filled her mind again, the man had been much too sure of himself in a
         place he had no right to be. Despite his confidence, though, her father would certainly send him on his way.
      </p>
<p>And after he did, she would not see Maxwell again. That thought, although it failed to cheer her, assured her that forgetting
         him was the only sensible thing to do.
      </p>
<p>Thus it was with astonishment and tingling trepidation that she found herself confronting him again unexpectedly that very
         afternoon.
      </p>
<p class="img"><img alt="art" src="images/Art_orn.jpg"/></p>
<p class="spb">Rob had not expected his visit to go smoothly, because as Will had pointed out, every Annandale laird took a certain pride
         in defying the Sheriff of Dumfries.
      </p>
<p>However, before Rob had left Dumfries, Alexander Maxwell had said in the stern, fatherly tone he used whenever he lectured
         his much younger brother, “No less than the power and reputation of Clan Maxwell are at stake in this matter, Rob. Dunwythie
         is clearly their leader, so your duty to the clan is plain. If we are ever to regain our power, you <em>must</em> make him understand the vast powers I command as hereditary sheriff, and put an end to the man’s defiance.”
      </p>
<p>Rob was well aware that the Maxwells’ power had waned for twenty years, since their loss of Caerlaverock Castle, the once
         mighty guardian of southwest Scotland. He knew, too, that Alex wanted above all else to reestablish that power.
      </p>
<p>Although fiercely loyal to his clan, and understanding his duty to his immediate family as well, Rob had not hesitated to
         express his doubt that anything less than a Maxwell army would impress the lairds of Annandale.
      </p>
<p>“Bless us, we don’t want war,” Alex retorted. “Use your head for once, lad!”</p>
<p>So, although he would have preferred to use his good right arm and a sword, Rob had racked his brain for another way to persuade
         Dunwythie to submit.
      </p>
<p>Thus far, none had occurred to him.</p>
<p>Sakes, he told himself now, Alex himself had already tried several times and failed. So much for the power of his office!</p>
<p>Aware that the impudent Will Jardine would be an unwanted distraction in any discussion with Dunwythie, Rob sent him back
         to Applegarth. Then, collecting the half dozen of his own men who had been watching his lordship, Rob headed for Dunwythie
         Mains with them that afternoon.
      </p>
<p>Approaching Dunwythie Mains from the south, as Rob and Will had earlier, Rob had seen only the estate’s extensive fields and
         woodlands. Dunwythie Hall was even more impressive. One would not be wrong to call it a castle.
      </p>
<p>Well fortified and strategically placed atop a wooded hill with the river Annan flowing in a sharp, protective bend below,
         the four-story keep loomed formidably above the stone walls of the bailey and its tall, ironbound gates. Rob noted that the
         ramparts commanded a view that must include a long stretch of the centuries-old Roman road that lay half a mile to the east.
         It ran much of the length of Annandale.
      </p>
<p>“Mayhap ye should leave two of our lads out here, laird,” the captain of his escort suggested as they neared the gates. “If
         we dinna come out again, they can report our capture to the sher—”
      </p>
<p>“Nay, for I come on official business and will meet no danger. You can all be of use to me, though, for I want each of you
         to learn what you can from anyone willing to talk. We need to know as much about this place and its owner as we can.”
      </p>
<p>Having no reason to refuse entrance to such a small party and doubtless aware that no reinforcements were in sight, men opened
         the gate at Rob’s shout.
      </p>
<p>Inside the bailey, he dismounted and assured gillies who came running that his men would see to their own horses. Then, taking
         off his sword belt and handing it to one of his men, to make plain that his visit was peaceful, he followed a young gillie
         of Dunwythie’s to the ironbound timber door of the keep.
      </p>
<p>The lad pushed the door open without ceremony, revealing a heavy iron yett against the wall behind it, ready to swing shut
         if attack threatened. The entryway was just a stair landing with stone steps leading up on Rob’s left and down to his right.
         The gillie turned Rob over to the porter, who told him that his lordship would receive him in the great hall.
      </p>
<p>“I’ll just be a-telling him your name when I take ye in, sir, if ye please.”</p>
<p>Rob gave his name as they mounted the stairs to an archway. Beyond it lay a great hall with colorful arras cloths hanging
         at the far end, framing a raised dais. A cheerful fire crackled in the large fireplace.
      </p>
<p>Seated at the central position behind the long table on the dais was a middle-aged man that Rob knew must be Dunwythie himself.</p>
<p>As Rob strode toward him, a door near the left end of the dais opened, and Dunwythie’s fair-haired daughter walked gracefully
         from the doorway to the dais. Lifting her skirts with her left hand as she stepped onto it, she had eyes only for her father
         as she approached him with a loving smile.
      </p>
<p>Dunwythie kept his gaze on Rob.</p>
<p>The lass, realizing that her father’s attention had fixed elsewhere, shifted her gaze accordingly. As it fell upon Rob, he
         anticipated a reaction. But, other than a slight pause, he saw none—not so much as the lifting of one fair eyebrow.
      </p>
<p>Amused by what he believed was rigid composure but admiring it, too, Rob returned his attention to his host.</p>
<p>The porter said, “Me lord, this be Robert Maxwell, Laird o’ Trailinghail and brother o’ yon vexatious Sheriff o’ Dumfries.”</p>
<p>The lass reacted then, biting her lower lip as she flicked a glance at Rob.</p>
<p>Meeting that twinkling look, he felt a reaction that shot to his toes and touched all points in between.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// SeducedByARogu_chap-1.vue?cbc88ee0